import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { BookOpen, ChevronLeft, ClipboardList, Monitor, Video } from 'lucide-react';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { fmtDate, naira } from '@/lib/format';
import { api } from '@/lib/api';
import { getChildrenAttendance, getChildrenById, getTeachers } from '@/api/parent';
import { type ChildCard, Empty, ErrorBox, Loading, Stat, field, unwrap } from '../ui';

interface Detail extends ChildCard {
  gender: string | null; date_of_birth: string | null; admitted_on: string | null; blood_group: string | null;
  form_teacher: { user_id: number; name: string } | null;
  other_guardians: { name: string; relationship: string | null }[];
}
interface Attendance { month: string; days: number; present: number; late: number; absent: number; excused: number; rate: number | null; records: { date: string; status: string; note: string | null }[] }
interface TeacherGroup { student_id: number; student: string; teachers: { user_id: number; name: string; form_teacher: boolean; subjects: string[] }[] }
interface Learning {
  active: boolean; period_days?: number;
  lessons?: { published: number; done: number; reading: number; recent: { id: number; topic: string; subject: string | null; published_at: string | null; state: 'not_started' | 'reading' | 'done' }[] };
  tests?: { taken: number; open: { id: number; title: string }[]; recent: { exam: string; score: number | null; submitted_at: string }[] };
  live?: { held: number; attended: number; upcoming: { id: number; title: string; starts_at: string; state: string; subject: string | null }[] };
  assignments?: { given: number; submitted: number; missed: number; due: { id: number; title: string; subject: string | null; due_at: string | null }[] };
}

const ATT: Record<string, string> = { present: 'bg-green-100 text-green-800', late: 'bg-amber-100 text-amber-800', absent: 'bg-red-100 text-red-800', excused: 'bg-blue-100 text-blue-800' };
const LESSON: Record<string, [string, string]> = { not_started: ['Not opened', 'text-slate-500'], reading: ['Reading', 'text-amber-700'], done: ['Finished', 'text-green-700'] };
const when = (s: string) => new Date(s).toLocaleString('en-NG', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

function Box({ title, icon: Icon, children }: { title: string; icon: any; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-4">
      <h2 className="font-bold text-slate-900 flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-slate-400" />{title}</h2>
      {children}
    </section>
  );
}

function LearningTab({ id }: { id: string }) {
  const q = useQuery({ queryKey: ['parent-learning', id], queryFn: () => unwrap<Learning>(api.get(`/parent/children/${id}/learning`)) });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;
  if (!d.active) return <Empty text="Learning activity shows while the child is in a class." />;
  const L = d.lessons!, T = d.tests!, V = d.live!, A = d.assignments!;
  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">The last {d.period_days} days.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Lessons finished" value={`${L.done}/${L.published}`} />
        <Stat label="Tests taken" value={T.taken} />
        <Stat label="Live classes attended" value={`${V.attended}/${V.held}`} />
        <Stat label="Homework submitted" value={`${A.submitted}/${A.given}`} />
      </div>
      {A.missed > 0 && <p className="text-sm bg-red-50 text-red-800 rounded-xl px-3 py-2">{A.missed} assignment{A.missed > 1 ? 's were' : ' was'} not submitted before the deadline.</p>}
      <div className="grid lg:grid-cols-2 gap-4">
        <Box title="Recent lessons" icon={BookOpen}>
          {!L.recent.length ? <Empty text="No lessons posted for the class yet." /> : (
            <ul className="divide-y divide-slate-100">
              {L.recent.map((l) => (
                <li key={l.id} className="py-2 flex items-center gap-3">
                  <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{l.topic}</p><p className="text-xs text-slate-500">{l.subject ?? '—'}{l.published_at ? ` · ${fmtDate(l.published_at)}` : ''}</p></div>
                  <span className={`text-xs font-semibold shrink-0 ${LESSON[l.state][1]}`}>{LESSON[l.state][0]}</span>
                </li>
              ))}
            </ul>
          )}
        </Box>
        <Box title="Homework due" icon={ClipboardList}>
          {!A.due.length ? <Empty text="Nothing outstanding." /> : (
            <ul className="divide-y divide-slate-100">
              {A.due.map((a) => <li key={a.id} className="py-2 flex items-center gap-3"><div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{a.title}</p><p className="text-xs text-slate-500">{a.subject ?? '—'}</p></div><span className="text-xs text-slate-500 shrink-0">{a.due_at ? `Due ${fmtDate(a.due_at)}` : 'No deadline'}</span></li>)}
            </ul>
          )}
        </Box>
        <Box title="Tests" icon={Monitor}>
          {T.open.length > 0 && <p className="text-sm bg-amber-50 text-amber-800 rounded-xl px-3 py-2 mb-2">Open now and not yet taken: {T.open.map((e) => e.title).join(', ')}</p>}
          {!T.recent.length ? <Empty text="No tests taken recently." /> : (
            <ul className="divide-y divide-slate-100">
              {T.recent.map((t, i) => <li key={i} className="py-2 flex items-center gap-3"><div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{t.exam}</p><p className="text-xs text-slate-500">{fmtDate(t.submitted_at)}</p></div><span className="text-sm font-bold shrink-0">{t.score != null ? t.score : '—'}</span></li>)}
            </ul>
          )}
        </Box>
        <Box title="Live classes coming up" icon={Video}>
          {!V.upcoming.length ? <Empty text="None scheduled." /> : (
            <ul className="divide-y divide-slate-100">
              {V.upcoming.map((c) => <li key={c.id} className="py-2 flex items-center gap-3"><div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{c.title}</p><p className="text-xs text-slate-500">{c.subject ?? ''}</p></div><span className={`text-xs shrink-0 ${c.state === 'live' ? 'text-red-600 font-bold' : 'text-slate-500'}`}>{c.state === 'live' ? 'LIVE now' : when(c.starts_at)}</span></li>)}
            </ul>
          )}
        </Box>
      </div>
    </div>
  );
}

function AttendanceTab({ id }: { id: string }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const att = useQuery({ queryKey: ['parent-attendance', id, month], queryFn: () => unwrap<Attendance>(getChildrenAttendance(id, { month })) });
  return (
    <div className="space-y-3">
      <input type="month" className={`${field} sm:!w-56`} value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
      {att.isLoading ? <Loading /> : att.isError || !att.data ? <ErrorBox error={att.error} onRetry={() => att.refetch()} /> : (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            <Stat label="Present" value={att.data.present} /><Stat label="Late" value={att.data.late} /><Stat label="Absent" value={att.data.absent} />
            <Stat label="Excused" value={att.data.excused} /><Stat label="Rate" value={att.data.rate != null ? `${att.data.rate}%` : '—'} />
          </div>
          <section className="bg-white rounded-2xl border border-slate-200">
            {!att.data.records.length ? <p className="p-4 text-sm text-slate-500">No attendance taken this month.</p> : (
              <ul className="divide-y divide-slate-100">
                {att.data.records.map((r) => (
                  <li key={r.date} className="px-4 py-2.5 flex items-center gap-3">
                    <span className="text-sm w-28 shrink-0">{new Date(r.date + 'T12:00:00').toLocaleDateString('en-NG', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${ATT[r.status] ?? 'bg-slate-100'}`}>{r.status}</span>
                    <span className="text-xs text-slate-500 truncate">{r.note}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export function ChildDetailPage() {
  const { id = '' } = useParams();
  const [tab, setTab] = useState<'learning' | 'attendance' | 'profile'>('learning');
  const child = useQuery({ queryKey: ['parent-child', id], queryFn: () => unwrap<Detail>(getChildrenById(id)) });
  const teachers = useQuery({ queryKey: ['parent-teachers'], queryFn: () => unwrap<TeacherGroup[]>(getTeachers()) });

  if (child.isLoading) return <Loading />;
  if (child.isError || !child.data) return <ErrorBox error={child.error} onRetry={() => child.refetch()} />;
  const c = child.data;
  const mine = teachers.data?.find((t) => String(t.student_id) === id);

  return (
    <div className="space-y-4 max-w-6xl">
      <div>
        <Link to="/parent/children" className="text-sm text-slate-500 inline-flex items-center"><ChevronLeft className="w-4 h-4" />Children</Link>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">{c.name}</h1>
        <p className="text-sm text-slate-500">{c.class ?? 'No class'} · {c.admission_no}</p>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <Stat label="Owing" value={c.owing > 0 ? `₦${Math.round(c.owing).toLocaleString()}` : 'Nil'} />
        <Stat label="This month" value={c.attendance_month?.rate != null ? `${c.attendance_month.rate}%` : '—'} />
        <Stat label="Results" value={c.results_held ? 'Held' : 'Open'} />
      </div>
      <div className="flex gap-2 overflow-x-auto text-sm">
        <Link to={`/parent/results?student=${id}`} className="px-3 py-2 rounded-xl border border-slate-200 font-semibold whitespace-nowrap">Results</Link>
        {c.can_pay && <Link to={`/parent/fees?student=${id}`} className="px-3 py-2 rounded-xl border border-slate-200 font-semibold whitespace-nowrap">Pay fees</Link>}
        <Link to="/parent/messages" className="px-3 py-2 rounded-xl border border-slate-200 font-semibold whitespace-nowrap">Message a teacher</Link>
        <Link to="/parent/document-requests" className="px-3 py-2 rounded-xl border border-slate-200 font-semibold whitespace-nowrap">Documents</Link>
      </div>

      <div className="flex border-b border-slate-200 gap-4 text-sm">
        {([['learning', 'Learning'], ['attendance', 'Attendance'], ['profile', 'Profile & teachers']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`pb-2 -mb-px border-b-2 font-semibold ${tab === k ? 'border-[#0A2540] text-[#0A2540]' : 'border-transparent text-slate-500'}`}>{l}</button>
        ))}
      </div>

      {tab === 'learning' && <LearningTab id={id} />}
      {tab === 'attendance' && <AttendanceTab id={id} />}
      {tab === 'profile' && (
        <div className="grid lg:grid-cols-2 gap-4">
          <section className="bg-white rounded-2xl border border-slate-200 p-4">
            <h2 className="font-bold mb-3">Profile</h2>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-slate-500">Status</dt><dd><StatusBadge status={c.status} /></dd>
              <dt className="text-slate-500">Gender</dt><dd className="capitalize">{c.gender ?? '—'}</dd>
              <dt className="text-slate-500">Date of birth</dt><dd>{c.date_of_birth ? fmtDate(c.date_of_birth) : '—'}</dd>
              <dt className="text-slate-500">Admitted</dt><dd>{c.admitted_on ? fmtDate(c.admitted_on) : '—'}</dd>
              <dt className="text-slate-500">Blood group</dt><dd>{c.blood_group ?? '—'}</dd>
              <dt className="text-slate-500">Form teacher</dt><dd>{c.form_teacher?.name ?? '—'}</dd>
              <dt className="text-slate-500">Other guardians</dt><dd>{c.other_guardians.length ? c.other_guardians.map((g) => `${g.name}${g.relationship ? ` (${g.relationship})` : ''}`).join(', ') : '—'}</dd>
            </dl>
          </section>
          <section className="bg-white rounded-2xl border border-slate-200 p-4">
            <h2 className="font-bold mb-3">Teachers</h2>
            {teachers.isLoading ? <Loading /> : !mine?.teachers.length ? <Empty text="No teachers assigned yet." /> : (
              <ul className="divide-y divide-slate-100">
                {mine.teachers.map((t) => (
                  <li key={t.user_id} className="py-2 flex items-center gap-3">
                    <div className="min-w-0 flex-1"><p className="text-sm font-medium">{t.name}{t.form_teacher && <span className="ml-2 text-[10px] font-bold uppercase text-green-700">Form teacher</span>}</p><p className="text-xs text-slate-500 truncate">{t.subjects.join(', ') || '—'}</p></div>
                    <Link to={`/parent/messages?to=${t.user_id}`} className="text-xs font-semibold text-[#0A2540] shrink-0">Message</Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
